import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import styles from './Card.module.css';

export type CardPadding = 'none' | 'compact' | 'default' | 'spacious';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Padding scale. Default: 'default' (24px). */
  padding?: CardPadding;
  /** Removes the default border. */
  borderless?: boolean;
  /** Renders the selected state visual. */
  selected?: boolean;
  /** Disables interactive states + dims the card. */
  disabled?: boolean;
  /** When true, the card responds to hover (lifts surface). Use for clickable cards. */
  interactive?: boolean;
  /** Card contents. */
  children: ReactNode;
}

/**
 * Container surface — used for form panels (auth screens), settings groups,
 * data summaries, list items. Borders by design system convention; no shadows.
 *
 * For the auth screens, the form panel uses `padding="spacious"` to match
 * the editorial brand-panel weight on the opposite side of the split layout.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    padding = 'default',
    borderless = false,
    selected = false,
    disabled = false,
    interactive = false,
    className,
    children,
    ...rest
  },
  ref,
) {
  const classes = [
    styles.card,
    styles[`padding_${padding}`],
    borderless ? styles.borderless : null,
    selected ? styles.selected : null,
    disabled ? styles.disabled : null,
    interactive ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} aria-disabled={disabled || undefined} {...rest}>
      {children}
    </div>
  );
});
